/**
 * MonitoringPickerBanner
 *
 * App-shell onboarding banner that surfaces when the team has at least
 * one verified URL and zero URLs actively monitored. Linked to
 * /monitoring/select.
 *
 * The banner is dismissible per-session (sessionStorage) so it
 * disappears once the user clicks the CTA or the X — but reappears in
 * a fresh session until they actually activate at least one URL (at
 * which point the count > 0 condition stops firing).
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { ShieldCheck, X } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import '@/design-system/shell.css'
import '@/design-system/support.css'
import { apiService } from '@/services/api'
import { useTeamContext } from '@/contexts/TeamContext'
import { useTierStatus } from '@/hooks/useTierStatus'

const DISMISS_KEY_PREFIX = 'monitoring-picker-banner-dismissed:'

export default function MonitoringPickerBanner() {
  const { currentTeam } = useTeamContext()
  const teamId = currentTeam?.team?.id
  // Per-team dismiss so switching teams in the same tab doesn't carry
  // a dismissal forward. Without scoping the key, a user dismissing
  // the banner on team A would never see it for newly-switched team B
  // until session reset.
  const dismissKey = teamId ? `${DISMISS_KEY_PREFIX}${teamId}` : null
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined' || !dismissKey) return false
    return sessionStorage.getItem(dismissKey) === '1'
  })

  // Re-read dismiss state when the team changes (covers in-tab team
  // switch). useState's initial value only fires once.
  useEffect(() => {
    if (typeof window === 'undefined' || !dismissKey) return
    setDismissed(sessionStorage.getItem(dismissKey) === '1')
  }, [dismissKey])

  // Cap query — gives us the team's plan cap and current active count.
  // Re-uses the same key as everywhere else so we don't double-fetch.
  //
  // Only for teams whose plan includes monitoring. The endpoint sits behind
  // requireMonitoring and answers 403 to everyone else, and this banner is
  // mounted in the app shell, so every free account used to log an API error
  // on every page it opened. The entitlement is already on the tier-usage
  // response every authenticated page fetches.
  const { entitlements } = useTierStatus()
  const canActivateMonitoring = entitlements?.canActivateMonitoring ?? false
  const { data: capData } = useQuery({
    queryKey: ['team-monitoring-cap'],
    queryFn: async () => {
      const res = await apiService.getTeamMonitoringCap()
      return res.data
    },
    staleTime: 60_000,
    enabled: canActivateMonitoring,
  })

  const currentCount = capData?.count ?? 0
  const cap = capData?.cap ?? 0

  // Only fetch the verified-not-active list when the gate is open —
  // avoids a wasted round-trip for the 99% of page loads where this
  // banner won't render. Gate: paid plan (cap > 0), no URLs actively
  // monitored yet (currentCount === 0).
  const shouldCheck = !dismissed && cap > 0 && currentCount === 0
  const { data: verifiedData } = useQuery({
    queryKey: ['monitoring-picker-verified-not-active', 'banner-probe'],
    queryFn: async () => {
      const res = await apiService.getVerifiedNotActiveUrls()
      return res.data
    },
    enabled: shouldCheck,
    staleTime: 5 * 60_000,
  })

  const verifiedCount = verifiedData?.urls.length ?? 0

  useEffect(() => {
    if (dismissed && dismissKey) sessionStorage.setItem(dismissKey, '1')
  }, [dismissed, dismissKey])

  // Render gate. Note `verifiedData === undefined` (probe still loading)
  // → null too, so the banner doesn't render → flash → re-render once
  // data arrives. We only mount when we know there's something to show.
  if (
    dismissed ||
    cap === 0 ||
    currentCount > 0 ||
    verifiedData === undefined ||
    verifiedCount === 0
  ) {
    return null
  }

  return (
    // The band runs full width and `ds-nudge__in` pads past the fixed rail.
    <DsIsland panel={false} ground={false} className="ds-nudge">
      <div className="ds-nudge__in">
        <ShieldCheck aria-hidden="true" />
        <div className="ds-nudge__say">
          <span className="ds-nudge__lead">
            You have {verifiedCount} verified URL{verifiedCount === 1 ? '' : 's'}.
          </span>{' '}
          <span className="ds-nudge__more">
            Pick which ones to actively monitor (up to {cap} on your plan).
          </span>
        </div>
        <Link
          to="/monitoring/select"
          className="ds-btn ds-btn--primary ds-btn--sm ds-nudge__go"
        >
          Pick URLs to monitor
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss banner"
          className="ds-nudge__x"
        >
          <X aria-hidden="true" />
        </button>
      </div>
    </DsIsland>
  )
}
